<script>
import { required, maxLength, requiredIf, email } from "@vuelidate/validators";
import { zipCode, numCarteFidelite } from "@/utils/form-validators";
import { useForms } from "@/components/organisms/forms/mixin";
import Button from "@/components/atoms/button";
import Form from "@/components/molecules/form";
import FormGroup from "@/components/molecules/form-group";
import FormInput from "@/components/molecules/form-input";
import {
  formValidationProps,
  useFormValidation
} from "@/components/organisms/mixins/form-validation.mixin";
import Captcha from "@/components/molecules/captcha";
import SuccessPopin from "@/components/organisms/forms/successPopin";
import Container from "@/components/atoms/container";
import ButtonGroup from "@/components/molecules/button-group";
import FormFieldset from "@/components/molecules/form-fieldset";

export default {
  name: "ProductInfosForm",
  components: {
    FormFieldset,
    ButtonGroup,
    Form,
    FormGroup,
    FormInput,
    Button,
    Captcha,
    SuccessPopin,
    Container
  },
  props: {
    ...formValidationProps()
  },
  emits: ["save"],
  setup(props) {
    const { captcha, v$, getState, getErrorMessage, checkValidation, states } =
      useFormValidation(props);

    const { redirectToPreviousPage } = useForms();

    return {
      redirectToPreviousPage,
      captcha,
      v$,
      getState,
      getErrorMessage,
      checkValidation,
      states
    };
  },
  data() {
    return {
      form: {
        num_carte: "",
        nom: "",
        prenom: "",
        adresse: "",
        cp: "",
        ville: "",
        email: ""
      },
      captchaToken: null
    };
  },
  validations() {
    return {
      form: {
        nom: {
          required
        },
        prenom: {
          required
        },
        adresse: {
          required: requiredIf(function () {
            return !this.form.email || this.form.cp || this.form.ville;
          })
        },
        cp: {
          required: requiredIf(function () {
            return !this.form.email || this.form.ville || this.form.adresse;
          }),
          zipCode
        },
        ville: {
          required: requiredIf(function () {
            return !this.form.email || this.form.cp || this.form.adresse;
          })
        },
        email: {
          required: requiredIf(function () {
            if (this.form.adresse && this.form.cp && this.form.ville) {
              return false;
            }
            return true;
          }),
          email,
          maxLength: maxLength(60)
        },
        num_carte: {
          required,
          numCarteFidelite
        }
      }
    };
  },
  methods: {
    save() {
      if (this.checkValidation(this.v$)) {
        this.$refs.captcha.execute();
      }
    },
    verifyCaptcha(token) {
      if (token) {
        this.captchaToken = token;
      }
      const model = {
        ...this.form,
        "g-captcha-response": this.captchaToken
      };
      this.$emit("save", model);
    }
  }
};
</script>
<template>
  <div class="u-mb-xlg u-pl-md u-pr-md u-bgcolor-white u-pb-md">
    <Container>
      <br>
      <p class="c-inscription-form__explanation">
        Vous souhaitez résilier votre adhésion au programme de fidélité cora ? Merci de compléter le formulaire suivant :
      </p>
      <br>
    </Container>
    <SuccessPopin
      v-if="state===states.SUCCESS"
      @hidden="redirectToPreviousPage()"
    />
    <Form
      :error="error"
      class="u-mx-xxlg u-mx-lg@md"
      @submit.prevent="save()"
    >
      <FormFieldset size="full">
        <FormGroup
          :state="getState(v$,'nom', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="nom"
            v-model.trim="form.nom"
            :state="getState(v$,'nom', 'form')"
            maxlength="60"
            size="full"
            @blur="v$.form.nom.$touch()"
            @input="v$.form.nom.$reset()"
          >
            Nom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.nom.required.$invalid">
                Merci de renseigner votre nom
              </template>
              <template v-else>
                {{ getErrorMessage("nom") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'prenom', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="prenom"
            v-model.trim="form.prenom"
            :state="getState(v$,'prenom', 'form')"
            maxlength="40"
            size="full"
            @blur="v$.form.prenom.$touch()"
            @input="v$.form.prenom.$reset()"
          >
            Prénom *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.prenom.required.$invalid">
                Merci de renseigner votre prénom
              </template>
              <template v-else>
                {{ getErrorMessage("prenom") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'email','form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="email"
            v-model.trim="form.email"
            :state="getState(v$,'email','form')"
            size="full"
            @blur="v$.form.email.$touch()"
            @input="v$.form.email.$reset()"
          >
            Email
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.email.required.$invalid">
                Merci de renseigner au moins votre adresse e-mail ou votre adresse postale complète
              </template>
              <template v-else-if="v$.form.email.email.$invalid">
                Le format de cette adresse e-mail est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("email") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'adresse', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="adresse"
            v-model.trim="form.adresse"
            :state="getState(v$,'adresse', 'form')"
            maxlength="255"
            size="full"
            @blur="v$.form.adresse.$touch()"
            @input="v$.form.adresse.$reset()"
          >
            Adresse
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.nom.required.$invalid">
                Merci de renseigner au moins votre adresse postale ou votre adresse e-mail
              </template>
              <template v-else>
                {{ getErrorMessage("adresse") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <FormGroup
          :state="getState(v$,'cp', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="cp"
            v-model.trim="form.cp"
            :state="getState(v$,'cp', 'form')"
            size="full"
            @blur="v$.form.cp.$touch()"
            @input="v$.form.cp.$reset()"
          >
            Code Postal
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.cp.required.$invalid">
                Merci de renseigner au moins votre code postal ou votre adresse e-mail
              </template>
              <template v-else>
                {{ getErrorMessage("cp") }}
              </template>
            </p>
          </template>
        </FormGroup>

        <FormGroup
          :state="getState(v$,'ville', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="ville"
            v-model.trim="form.ville"
            :state="getState(v$,'ville', 'form')"
            size="full"
            @blur="v$.form.ville.$touch()"
            @input="v$.form.ville.$reset()"
          >
            Ville
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.ville.required.$invalid">
                Merci de renseigner au moins votre ville ou votre adresse e-mail
              </template>
              <template v-else>
                {{ getErrorMessage("ville") }}
              </template>
            </p>
          </template>
        </Formgroup>
        <FormGroup
          :state="getState(v$,'num_carte', 'form')"
          class="c-inscription-form__form-group"
          size="full"
        >
          <FormInput
            id="num_carte"
            v-model="form.num_carte"
            :state="getState(v$,'num_carte', 'form')"
            size="full"
            @blur="v$.form.num_carte.$touch()"
            @input="v$.form.num_carte.$reset()"
          >
            N° de carte de fidélité *
          </FormInput>
          <template #message>
            <p>
              <template v-if="v$.form.num_carte.numCarteFidelite.$invalid">
                Ce numéro de carte est invalide
              </template>
              <template v-else>
                {{ getErrorMessage("num_carte") }}
              </template>
            </p>
          </template>
        </FormGroup>
        <p class="c-inscription-form__required-field">
          Champs obligatoires*
        </p>
        <Captcha
          ref="captcha"
          @verify="verifyCaptcha"
        />
        <ButtonGroup
          center
          class="u-mb-md"
        >
          <Button
            variant="secondary"
            radius="full"
            type="submit"
            value="Valider ma résiliation"
            size="md"
            :disabled="state === states.LOADING"
            class="u-mt-sm u-flex-justify-center@sm"
          />
        </ButtonGroup>
      </FormFieldset>
    </Form>
    <Container>
      <div class="c-inscription-form__explanation">
        <p class="u-mb-xs">
          Pour rappel, en résiliant votre programme de fidélité :
        </p>
        <ul>
          <li>Votre compte de fidélité sera clôturé.</li>
          <li>Cette action est définitive et sera effective dans un délai maximal de 7 jours ouvrés.</li>
          <li>Vous perdrez les avantages cumulés liés à votre compte de fidélité.</li>
          <li>Si vous disposez d'une carte de fidélité physique, vous devrez la remettre à votre magasin de rattachement.</li>
          <li>Vous ne recevrez plus les communications concernant le programme de fidélité par SMS ou par mail de la part de Cora.</li>
        </ul>
      </div>
    </Container>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/inscription-form.scss";
</style>
